/**
 * Validation for Application Insights connection strings, per the
 * Microsoft Learn specification:
 * https://learn.microsoft.com/en-us/azure/azure-monitor/app/sdk-connection-string
 *
 *   - A connection string is a semicolon-delimited list of `key=value` pairs.
 *   - Max length is 4096 characters.
 *   - `InstrumentationKey` is required and must be a GUID.
 *   - `Authorization`, when present, only supports the value `ikey` today.
 *   - Endpoint overrides (`IngestionEndpoint`, `LiveEndpoint`,
 *     `ProfilerEndpoint`, `SnapshotEndpoint`) must be absolute URLs.
 *   - `EndpointSuffix`, when present, must look like a domain name.
 *   - `ApplicationId` is set automatically by the platform and is allowed
 *     to pass through without further validation.
 *   - Other unknown keys are tolerated so this client doesn't break when
 *     Microsoft adds new optional pairs (the docs explicitly call this out).
 */

/** Maximum length accepted by the observability backend, per the AI spec. */
export const MAX_CONNECTION_STRING_LENGTH = 4096;

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN_REGEX = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

const ENDPOINT_KEYS = ['ingestionendpoint', 'liveendpoint', 'profilerendpoint', 'snapshotendpoint'];

export interface ConnectionStringValidationResult {
  ok: boolean;
  /** Human-readable error message — safe to display in a Field validationMessage. */
  message?: string;
}

const isValidAbsoluteUrl = (value: string): boolean => {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validate an Application Insights connection string against the rules the
 * Microsoft Application Insights specification enforces. Returns
 * `{ ok: true }` on success or `{ ok: false, message }` describing the
 * first problem found.
 */
export const validateAppInsightsConnectionString = (
  value: string | null | undefined,
): ConnectionStringValidationResult => {
  if (value == null || value.trim() === '') {
    return { ok: false, message: 'Connection string is required.' };
  }
  if (value.length > MAX_CONNECTION_STRING_LENGTH) {
    return {
      ok: false,
      message: `Connection string must be ${MAX_CONNECTION_STRING_LENGTH} characters or fewer.`,
    };
  }

  const segments = value
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  if (segments.length === 0) {
    return { ok: false, message: 'Connection string is required.' };
  }

  const pairs: { key: string; value: string }[] = [];
  for (const segment of segments) {
    const eq = segment.indexOf('=');
    if (eq <= 0) {
      return {
        ok: false,
        message: `Each segment must be "key=value". Invalid segment: "${segment}".`,
      };
    }
    const key = segment.slice(0, eq).trim();
    const segValue = segment.slice(eq + 1).trim();
    if (!/^[A-Za-z][A-Za-z0-9]*$/.test(key)) {
      return {
        ok: false,
        message: `"${key}" is not a valid connection-string key.`,
      };
    }
    pairs.push({ key, value: segValue });
  }

  const lookup = new Map<string, string>(
    pairs.map(({ key, value: v }) => [key.toLowerCase(), v]),
  );

  const instrumentationKey = lookup.get('instrumentationkey');
  if (instrumentationKey === undefined) {
    return {
      ok: false,
      message: 'Connection string must contain an "InstrumentationKey=<GUID>" segment.',
    };
  }
  if (instrumentationKey === '') {
    return {
      ok: false,
      message: 'The InstrumentationKey segment must contain a GUID value.',
    };
  }
  if (!GUID_REGEX.test(instrumentationKey)) {
    return {
      ok: false,
      message: 'InstrumentationKey must be a valid GUID (e.g. 00000000-0000-0000-0000-000000000000).',
    };
  }

  const authorization = lookup.get('authorization');
  if (authorization !== undefined && authorization.toLowerCase() !== 'ikey') {
    return {
      ok: false,
      message: 'Authorization, when specified, must be "ikey".',
    };
  }

  for (const endpointKey of ENDPOINT_KEYS) {
    const endpointValue = lookup.get(endpointKey);
    if (endpointValue !== undefined) {
      if (endpointValue === '' || !isValidAbsoluteUrl(endpointValue)) {
        const original = pairs.find((p) => p.key.toLowerCase() === endpointKey)?.key ?? endpointKey;
        return {
          ok: false,
          message: `${original} must be an absolute http(s) URL.`,
        };
      }
    }
  }

  const endpointSuffix = lookup.get('endpointsuffix');
  if (endpointSuffix !== undefined) {
    if (endpointSuffix === '' || !DOMAIN_REGEX.test(endpointSuffix)) {
      return {
        ok: false,
        message: 'EndpointSuffix must be a valid domain name (for example "applicationinsights.us").',
      };
    }
  }

  return { ok: true };
};

